<template>
    <div id="document-by-pro-datatable-component" class="min-height-component-550">
        <div v-if="$store.getters['user_connected/user_connected'].id_professional === 14" class="mb-6">
            <p class="mb-0">{{ $t('document_by_pro_component.tutorial_videos') }} :</p>
            <div class="pl-4">
                <div>
                    <a href="https://youtu.be/oZFxeA2a7qw" target="_blank">
                        {{ $t('document_by_pro_component.cartidge_filter_replacement') }}
                    </a>
                </div>
                <div>
                    <a href="https://youtu.be/wDvhmA-LNTU" target="_blank">
                        {{ $t('document_by_pro_component.qfac_filter_replacement') }}
                    </a>
                </div>
            </div>
        </div>

        <template v-if="!loading_pro_documents">
            <DocumentCategoryCardComponent
                :categories="paginated_file_categories"
                :page="file_categories_page"
                :per-page="file_categories_per_page"
                :selected="selected_file_categories"
                :total="file_categories.length"
                v-on:toggle="toggleFileCategory"
                v-on:update:page="file_categories_page = $event"
            />

            <template v-if="pro_documents.length > 0">
                <div class="grid">
                    <v-row>
                        <v-col v-for="document in filtered_pro_documents" :key="document.id_file" cols="12" lg="4" md="6" xl="3">
                            <v-card elevation="0">
                                <v-card-text>
                                    <div class="gap-1 mb-2 d-flex justify-space-between align-center">
                                        <v-tooltip open-delay="500" top>
                                            <template v-slot:activator="{ on, attrs }">
                                                <span class="text-overflow-ellipsis" v-bind="attrs" v-on="on">
                                                    {{ document.filename }}
                                                </span>
                                            </template>
                                            <span class="white--text">
                                                {{ document.filename }}
                                            </span>
                                        </v-tooltip>

                                        <MenuActionsComponent
                                            :actions="[
                                                {
                                                    title: $t('document_by_pro_component.download_document'),
                                                    icon: 'mdi-download',
                                                    emit: 'downloadUsefulDocuments',
                                                    has_access: true,
                                                },
                                                {
                                                    title: $t('document_by_pro_component.overview'),
                                                    icon: 'mdi-eye',
                                                    emit: 'prepareOpenDialogPreview',
                                                    has_access: true,
                                                },
                                            ]"
                                            v-on:downloadUsefulDocuments="downloadUsefulDocuments(document)"
                                            v-on:prepareOpenDialogPreview="prepareOpenDialogPreview(document)"
                                        />
                                    </div>

                                    <div
                                        v-if="document.file_categories && document.file_categories.length"
                                        class="d-flex flex-wrap gap-1 mb-2"
                                    >
                                        <v-chip
                                            v-for="category in document.file_categories"
                                            :key="category.id"
                                            label
                                            small
                                        >
                                            {{ category.category_name }}
                                        </v-chip>
                                    </div>

                                    <pdf :src="document.path_file" style="overflow: hidden; height: 300px; border-radius: 4px" />
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <!-- START PREVIEW DOCUMENT -->
                <v-dialog v-model="dialog_preview_pdf" max-width="700" scrollable>
                    <v-card>
                        <v-card-text class="pa-1">
                            <pdf :page="index_page_to_display" :src="selected_file.path_file" />
                            <v-progress-circular v-if="loading_my_file_pdf" class="pdf-loader" color="primary" indeterminate />
                        </v-card-text>

                        <v-divider />
                        <v-card-actions class="d-flex justify-space-between">
                            <v-icon @click="1 < index_page_to_display ? index_page_to_display-- : null"> mdi-chevron-left </v-icon>
                            <v-icon @click="total_pages > index_page_to_display ? index_page_to_display++ : null">
                                mdi-chevron-right
                            </v-icon>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <v-dialog v-model="dialog_preview_picture" max-width="700">
                    <v-card>
                        <v-card-text class="pa-1">
                            <v-img :src="selected_file.path_file">
                                <template v-slot:placeholder>
                                    <v-row align="center" class="fill-height ma-0" justify="center">
                                        <v-progress-circular color="primary" indeterminate />
                                    </v-row>
                                </template>
                            </v-img>
                        </v-card-text>
                    </v-card>
                </v-dialog>
                <!-- END PREVIEW DOCUMENT -->
            </template>

            <template v-else>
                <EmptyPageComponent
                    :link_img="'/images/error/empty_page.png'"
                    :main_text="$t('document_by_pro_component.no_document_yet')"
                />
            </template>
        </template>

        <template v-else>
            <div class="grid">
                <v-row>
                    <v-col v-for="n in 4" :key="n" cols="12" lg="4" md="6" xl="3">
                        <v-skeleton-loader type="image" />
                    </v-col>
                </v-row>
            </div>
        </template>
    </div>
</template>

<script>
import FileRepository from '@/repositories/FileRepository'
import pdf from 'vue-pdf'
import EmptyPageComponent from '@/components/global/error/EmptyPageComponent'
import MenuActionsComponent from '@/components/global/containment/MenuActionsComponent.vue'
import DocumentCategoryCardComponent from '@/components/document/DocumentCategoryCardComponent.vue'

export default {
    name: 'DocumentByProDatatableComponent',
    components: { DocumentCategoryCardComponent, MenuActionsComponent, EmptyPageComponent, pdf },
    data() {
        return {
            search: '',
            pro_documents: [],
            loading_pro_documents: false,
            selected_pro_documents: [],
            headers: [
                { text: this.$t('document_by_pro_component.document_name'), value: 'filename', filterable: true },
                {
                    text: this.$t('document_by_pro_component.publication_date'),
                    value: 'date_creation',
                    filterable: true,
                },
                { text: this.$t('global.actions'), value: 'actions', filterable: true },
            ],

            selected_file_categories: [],
            file_categories_page: 1,
            file_categories_per_page: 12,

            selected_file: '',
            dialog_preview_picture: false,
            dialog_preview_pdf: false,
            loading_my_file_pdf: false,
            total_pages: 0,
            index_page_to_display: 1,
        }
    },
    computed: {
        file_categories() {
            const categories = new Map()

            this.pro_documents.forEach((document) => {
                const documentCategories = document.file_categories ?? []

                // count files by categories
                documentCategories.forEach((category) => {
                    const known = categories.get(category.id)

                    if (known) {
                        known.document_count++
                    } else {
                        categories.set(category.id, { ...category, document_count: 1 })
                    }
                })
            })

            return [...categories.values()].sort((first, second) => first.category_name.localeCompare(second.category_name))
        },
        paginated_file_categories() {
            const start = (this.file_categories_page - 1) * this.file_categories_per_page

            return this.file_categories.slice(start, start + this.file_categories_per_page)
        },
        filtered_pro_documents() {
            let documents = this.pro_documents

            if (this.selected_file_categories.length > 0) {
                documents = this.pro_documents.filter((document) =>
                    (document.file_categories ?? []).some((category) => this.selected_file_categories.includes(category.id))
                )
            }

            return documents
        },
    },
    created() {
        this.getUsefulDocuments()
    },
    methods: {
        toggleFileCategory(category) {
            if (this.selected_file_categories.includes(category.id)) {
                this.selected_file_categories = this.selected_file_categories.filter((id) => id !== category.id)
            } else {
                this.selected_file_categories = [...this.selected_file_categories, category.id]
            }
        },
        getUsefulDocuments() {
            this.loading_pro_documents = true

            FileRepository.getByClientAssetsAndModuleDesignation('PWA_DOCUMENT')
                .then((success) => {
                    this.pro_documents = success.data.data
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.loading_pro_documents = false
                })
        },

        downloadUsefulDocuments(item) {
            let data = []
            if (this.selected_pro_documents.length > 0) {
                data = JSON.parse(JSON.stringify(this.selected_pro_documents))
            } else if (item) {
                data.push(item)
            } else {
                data = this.pro_documents
            }

            if (data.length > 0) {
                data.forEach((file) => {
                    this.downloadFile(file, 'PWA_DOCUMENT')
                })
            }
        },

        prepareOpenDialogPreview(file) {
            this.selected_file = file
            if (file.file_type.type === 'application/pdf') {
                this.openDialogPreviewPdf()
            } else {
                this.openDialogPreviewPicture()
            }
        },

        openDialogPreviewPicture() {
            this.dialog_preview_picture = true
        },

        openDialogPreviewPdf() {
            this.index_page_to_display = 1
            this.loading_my_file_pdf = true
            this.dialog_preview_pdf = true
            pdf.createLoadingTask(this.selected_file.path_file).promise.then((document) => {
                this.total_pages = document.numPages
                this.loading_my_file_pdf = false
            })
        },
    },
}
</script>
